import AttachmentDataProviderItem from 'owa-service/lib/contract/AttachmentDataProviderItem';
import AttachmentDataProviderItemType from 'owa-service/lib/contract/AttachmentDataProviderItemType';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import AttachmentItemsSortOrder from 'owa-service/lib/contract/AttachmentItemsSortOrder';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/GetAttachmentDataProviderItemsResponse';
import handleGetItemsResponse from './handleGetItemsResponse';
import { assertNever } from 'owa-assert';
import { getMimeTypeForOneDriveItem } from '../utils/getMimeTypeForOneDriveItem';
import { parse as parseUrl } from 'url';
import {
    oneDriveProItemsSkipTokenPagingMetadata,
    OneDriveProItemsSkipTokenPagingMetadata,
} from '../types/OneDriveProItemsSkipTokenPagingMetadata';
import {
    FileAttachmentDataProviderItemType,
    FolderAttachmentDataProviderItemType,
} from '../constants';
import {
    getOneDriveItems,
    GetOneDriveItemsPagingInfo,
    ONE_DRIVE_CONUSMER_API_URL,
    OneDriveItem,
} from 'owa-fileprovider-services';

function createFailedAttachmentDataProviderItemsResponse(
    errorCode: AttachmentResultCode
): GetAttachmentDataProviderItemsResponse {
    return {
        TotalItemCount: 0,
        ResultCode: errorCode,
        Items: [],
    };
}

function createAttachmentDataProviderItemForOneDriveItem(
    item: OneDriveItem,
    endpointUrl: string,
    fileProviderId: string,
    providerType: AttachmentDataProviderType
): AttachmentDataProviderItem {
    const [typeOfObject, mimeType, childCount, type, size] = item.folder
        ? [
              FolderAttachmentDataProviderItemType,
              null,
              item.folder.childCount,
              AttachmentDataProviderItemType.Folder,
              0, // This API returns the size of a folder as the sum of the sizes of the files in the folder.
              // We want to set this to 0 instead to be consistent with other APIs/providers.
          ]
        : [
              FileAttachmentDataProviderItemType,
              getMimeTypeForOneDriveItem(item),
              0,
              AttachmentDataProviderItemType.File,
              item.size,
          ];
    return {
        __type: typeOfObject,
        Id: item.id,
        Name: item.name,
        Size: size,
        Thumbnail: {
            Url:
                item.thumbnails && item.thumbnails[0] && item.thumbnails[0].medium
                    ? item.thumbnails[0].medium.url
                    : null,
        },
        Modified: item.lastModifiedDateTime,
        ModifiedBy:
            item.lastModifiedBy && item.lastModifiedBy.user
                ? item.lastModifiedBy.user.displayName
                : null,
        MimeType: mimeType,
        Location: parseUrl(item.webDavUrl).path,
        ProviderEndpointUrl: endpointUrl,
        ChildCount: childCount,
        AttachmentProviderId: fileProviderId,
        ProviderType: providerType,
        Type: type,
    };
}

function createAttachmentDataProviderItemsResponse(
    items: OneDriveItem[],
    endpointUrl: string,
    fileProviderId: string,
    providerType:
        | AttachmentDataProviderType.OneDrivePro
        | AttachmentDataProviderType.OneDriveConsumer,
    newSkipToken: string | null,
    previousPagingMetadata: OneDriveProItemsSkipTokenPagingMetadata | null
): GetAttachmentDataProviderItemsResponse {
    let pagingMetadata: OneDriveProItemsSkipTokenPagingMetadata = undefined;

    if (newSkipToken) {
        switch (providerType) {
            case AttachmentDataProviderType.OneDrivePro:
                const totalItemsFetched = previousPagingMetadata
                    ? previousPagingMetadata.totalItemsFetched + items.length
                    : items.length;
                pagingMetadata = oneDriveProItemsSkipTokenPagingMetadata({
                    skipToken: newSkipToken,
                    totalItemsFetched: totalItemsFetched,
                });
                break;
            case AttachmentDataProviderType.OneDriveConsumer:
                throw new Error('We do not support skip token based paging for OneDriveConsumer');
            default:
                assertNever(providerType);
        }
    }

    let totalItemsCount: number = items.length; // The default total items is the length of items we received when there is no page
    if (newSkipToken && pagingMetadata) {
        // If there is a next page then use the paging metadata to get the total
        // count of items fetched till now and we add 1 more to it to indicate there are more items
        totalItemsCount = pagingMetadata.totalItemsFetched + 1;
    } else if (pagingMetadata) {
        // If there is no skip token but there was paging metadata then use the total count from it
        totalItemsCount = pagingMetadata.totalItemsFetched;
    }

    return {
        TotalItemCount: totalItemsCount,
        ResultCode: AttachmentResultCode.Success,
        Items: items.map(item =>
            createAttachmentDataProviderItemForOneDriveItem(
                item,
                endpointUrl,
                fileProviderId,
                providerType
            )
        ),
        PagingMetadata: pagingMetadata,
    };
}

function getResponse(
    errorResponse: Response,
    result: OneDriveItem[],
    endpointUrl: string,
    fileProviderId: string,
    providerType:
        | AttachmentDataProviderType.OneDrivePro
        | AttachmentDataProviderType.OneDriveConsumer,
    skipToken: string | null,
    previousPagingMetadata: OneDriveProItemsSkipTokenPagingMetadata | null
): GetAttachmentDataProviderItemsResponse {
    if (result) {
        return handleGetItemsResponse(
            result,
            null /* errorResponse */,
            (items: OneDriveItem[]) =>
                createAttachmentDataProviderItemsResponse(
                    items,
                    endpointUrl,
                    fileProviderId,
                    providerType,
                    skipToken,
                    previousPagingMetadata
                ),
            createFailedAttachmentDataProviderItemsResponse
        );
    } else {
        return handleGetItemsResponse<OneDriveItem[]>(
            null /* result */,
            errorResponse,
            (items: OneDriveItem[]) =>
                createAttachmentDataProviderItemsResponse(
                    items,
                    endpointUrl,
                    fileProviderId,
                    providerType,
                    skipToken,
                    previousPagingMetadata
                ),
            createFailedAttachmentDataProviderItemsResponse
        );
    }
}

export async function getOneDriveProFileProviderItems(
    endpointUrl: string,
    fileProviderId: string,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    pagingMetadata: { metadata: OneDriveProItemsSkipTokenPagingMetadata; maxEntries: number } = null
): Promise<GetAttachmentDataProviderItemsResponse> {
    const endpointParsedUrl = parseUrl(endpointUrl);
    const endpointDomain = `${endpointParsedUrl.protocol}//${endpointParsedUrl.host}`;

    const pagingInfo: GetOneDriveItemsPagingInfo | null = pagingMetadata
        ? {
              maxEntries: pagingMetadata.maxEntries,
              skipToken: pagingMetadata.metadata ? pagingMetadata.metadata.skipToken : null,
          }
        : null;

    const {
        errorResponse,
        result: { items, skipToken },
    } = await getOneDriveItems(
        endpointDomain,
        AttachmentDataProviderType.OneDrivePro,
        sortColumn,
        sortOrder,
        folderId,
        undefined /* filter */,
        pagingInfo
    );

    const response = getResponse(
        errorResponse,
        items,
        endpointUrl,
        fileProviderId,
        AttachmentDataProviderType.OneDrivePro,
        skipToken,
        pagingMetadata ? pagingMetadata.metadata : null
    );

    return response;
}

export async function getOneDriveConsumerFileProviderItems(
    fileProviderId: string,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string
): Promise<GetAttachmentDataProviderItemsResponse> {
    const {
        errorResponse,
        result: { items },
    } = await getOneDriveItems(
        ONE_DRIVE_CONUSMER_API_URL,
        AttachmentDataProviderType.OneDriveConsumer,
        sortColumn,
        sortOrder,
        folderId
    );

    return getResponse(
        errorResponse,
        items,
        ONE_DRIVE_CONUSMER_API_URL,
        fileProviderId,
        AttachmentDataProviderType.OneDriveConsumer,
        null /* skipToken */,
        null /* pagingMetadata */
    );
}
